export enum HoldedDocType {
  INVOICE = 'invoice',
  SALESRECEIPT = 'salesreceipt',
  CREDITNOTE = 'creditnote',
  SALESORDER = 'salesorder',
  PROFORM = 'proform',
  WAYBILL = 'waybill',
  ESTIMATE = 'estimate',
  PURCHASE = 'purchase',
  PURCHASEORDER = 'purchaseorder',
  PURCHASEREFUND = 'purchaserefund',
}

export interface HoldedStatus {
  connected: boolean
  docTypeCreditNote: HoldedDocType
  approveDoc: boolean
  autoSendDocuments: boolean
  autoExport: {
    onCreate: boolean
    onUpdate?: boolean
    onPaid: boolean
    onCancel: boolean
  }
}

export interface HoldedSettings {
  docTypeCreditNote?: HoldedDocType
  approveDoc?: boolean
  autoSendDocuments?: boolean
  autoExport?: {
    onCreate?: boolean
    onUpdate?: boolean
    onPaid?: boolean
    onCancel?: boolean
  }
}

export interface HoldedExportPayload {
  invoicePkeys: string[]
}

export interface HoldedExportedInvoice {
  attributes?: {
    integrations?: {
      holded_exported_at?: number
      holded_invoice_id?: string
    }
  }
}

export function isInvoiceExportedToHolded(invoice: HoldedExportedInvoice): boolean {
  return !!invoice.attributes?.integrations?.holded_exported_at
}

export enum HoldedActivityType {
  ConnectionTest = 'connection_test',
  ExportSingleObject = 'export_single_object',
  ExportBatchObjects = 'export_batch_objects',
  ExportAllTypes = 'export_all_types',
  DeleteSingleObject = 'delete_single_object',
}

export enum HoldedActivityStatus {
  InProgress = 'in_progress',
  Completed = 'completed',
  Failed = 'failed',
}

export interface HoldedActivityLogMetadata {
  pkeys?: string[]
  types?: string[]
}

export interface HoldedActivityLogItem {
  _id: string
  jobId: string
  account: number
  metadata?: HoldedActivityLogMetadata
  activityType: HoldedActivityType
  status: HoldedActivityStatus
  errorMessage?: string
  completedAt?: string
  createdAt: string
  updatedAt: string
}

export interface HoldedActivityLogViewItem extends HoldedActivityLogItem {
  title: string
  description: string
  color: string
}

export interface HoldedActivityLogsResponse {
  data: HoldedActivityLogItem[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export interface HoldedActivityLogRequest {
  page?: number
  limit?: number
  activityType?: HoldedActivityType
  status?: HoldedActivityStatus
}

export interface HoldedTestConnectionResponse {
  success: boolean
  jobId: string
  error?: string
}

export function getHoldedActivityLogStatusColor(
  status: HoldedActivityStatus
): 'success' | 'danger' | 'warning' | 'secondary' {
  if (status === HoldedActivityStatus.Completed) {
    return 'success'
  } else if (status === HoldedActivityStatus.Failed) {
    return 'danger'
  } else if (status === HoldedActivityStatus.InProgress) {
    return 'warning'
  }
  return 'secondary'
}

export function getHoldedActivityLogStatus(status: HoldedActivityStatus): string {
  if (status === HoldedActivityStatus.Completed) {
    return $localize`Success`
  } else if (status === HoldedActivityStatus.Failed) {
    return $localize`Failed`
  } else if (status === HoldedActivityStatus.InProgress) {
    return $localize`Pending`
  }
  return $localize`Unknown`
}

export function getHoldedActivityLogTitle(item: HoldedActivityLogItem): string {
  const status = getHoldedActivityLogStatus(item.status)
  switch (item.activityType) {
    case HoldedActivityType.ConnectionTest:
      return $localize`Connection Test` + ` ${status}`
    case HoldedActivityType.ExportSingleObject:
    case HoldedActivityType.ExportBatchObjects:
      return $localize`Sync` + ` ${status}`
    case HoldedActivityType.ExportAllTypes:
      return $localize`Sync All Data` + ` ${status}`
    case HoldedActivityType.DeleteSingleObject:
      return $localize`Deleted` + ` ${status}`
    default:
      return $localize`Unknown Activity` + ` ${status}`
  }
}

export function getHoldedActivityLogDescription(item: HoldedActivityLogItem): string {
  switch (item.activityType) {
    case HoldedActivityType.ConnectionTest:
      if (item.status === HoldedActivityStatus.Completed) {
        return $localize`AdvanceCM is connected to Holded`
      } else if (item.status === HoldedActivityStatus.Failed) {
        return $localize`Unable to verify Holded connection, please check your settings.`
      } else if (item.status === HoldedActivityStatus.InProgress) {
        return $localize`Verifying Holded connection...`
      }
      return ''
    case HoldedActivityType.ExportSingleObject:
    case HoldedActivityType.ExportBatchObjects:
      if (item.status === HoldedActivityStatus.Completed) {
        return $localize`Export to Holded completed successfully`
      } else if (item.status === HoldedActivityStatus.Failed) {
        return item.errorMessage || $localize`Failed to export to Holded`
      } else if (item.status === HoldedActivityStatus.InProgress) {
        return $localize`Exporting to Holded...`
      }
      return ''
    case HoldedActivityType.ExportAllTypes:
      if (item.status === HoldedActivityStatus.Completed) {
        return $localize`All data synced to Holded`
      } else if (item.status === HoldedActivityStatus.Failed) {
        return item.errorMessage || $localize`Failed to sync all data to Holded`
      } else if (item.status === HoldedActivityStatus.InProgress) {
        return $localize`Syncing all data to Holded...`
      }
      return ''
    case HoldedActivityType.DeleteSingleObject:
      if (item.status === HoldedActivityStatus.Completed) {
        return $localize`Object deleted from Holded`
      } else if (item.status === HoldedActivityStatus.Failed) {
        return item.errorMessage || $localize`Failed to delete object from Holded`
      } else if (item.status === HoldedActivityStatus.InProgress) {
        return $localize`Deleting object from Holded...`
      }
      return ''
    default:
      return ''
  }
}
